import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Space, Divider, Alert } from 'antd';
import { GoogleOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface LoginProps {
  onSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSuccess }) => {
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleEmailLogin = async (values: { email: string; password: string }) => {
    try {
      setLoading(true);
      setError(null);
      
      if (isSignup) {
        await signup(values.email, values.password);
      } else {
        await login(values.email, values.password);
      }
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Authentication error:', err);
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      await loginWithGoogle();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Google authentication error:', err);
      let errorMessage = 'Google sign-in failed. Please try again.';
      
      if (err.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized. Please add localhost to Firebase authorized domains in the console.';
      } else if (err.code === 'auth/popup-blocked') {
        errorMessage = 'Pop-up was blocked. Please allow pop-ups for this site.';
      } else if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: 'calc(100vh - 100px)',
      padding: '20px' 
    }}>
      <Card style={{ width: '100%', maxWidth: 400 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2}>{isSignup ? 'Create Account' : 'Sign In'}</Title>
            <Text type="secondary">
              {isSignup 
                ? 'Create an account to access Innskytingsliste' 
                : 'Sign in to access Innskytingsliste'}
            </Text>
          </div>

          {error && (
            <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} />
          )}

          <Button
            type="primary"
            icon={<GoogleOutlined />}
            size="large"
            block
            onClick={handleGoogleLogin}
            loading={loading}
          >
            Continue with Google
          </Button>

          <Divider>or</Divider>

          <Form
            name="login"
            onFinish={handleEmailLogin}
            layout="vertical"
            autoComplete="off"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input 
                prefix={<MailOutlined />} 
                placeholder="Email" 
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter your password' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
            >
              <Input.Password 
                prefix={<LockOutlined />} 
                placeholder="Password" 
                size="large"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                {isSignup ? 'Sign Up' : 'Sign In'}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center' }}>
            <Text>
              {isSignup ? 'Already have an account? ' : "Don't have an account? "}
              <Button 
                type="link" 
                onClick={() => {
                  setIsSignup(!isSignup);
                  setError(null);
                }}
                disabled={loading}
              >
                {isSignup ? 'Sign In' : 'Sign Up'}
              </Button>
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Login;
